import type {
  SalesAttributionFilterState,
  SalesAttributionOption,
  SalesAttributionUtmCombination,
} from "./SalesAttributionTypes";

export type SalesAttributionUtmField = "utmSource" | "utmMedium" | "utmCampaign";

const UTM_FIELDS: SalesAttributionUtmField[] = ["utmSource", "utmMedium", "utmCampaign"];

const COMBINATION_KEY: Record<SalesAttributionUtmField, keyof SalesAttributionUtmCombination> = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
};

export const isUtmField = (field: string): field is SalesAttributionUtmField =>
  UTM_FIELDS.some(utmField => utmField === field);

export const getUtmOptions = (
  combinations: SalesAttributionUtmCombination[],
  field: SalesAttributionUtmField,
  filters: SalesAttributionFilterState
): SalesAttributionOption[] => {
  const values = new Set<string>();
  combinations.forEach(combination => {
    const matchesOtherSelections = UTM_FIELDS.every(
      other =>
        other === field || !filters[other] || combination[COMBINATION_KEY[other]] === filters[other]
    );
    const value = combination[COMBINATION_KEY[field]];
    if (matchesOtherSelections && value) {
      values.add(value);
    }
  });
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ value, label: value }));
};

export const reconcileUtmFilters = (
  combinations: SalesAttributionUtmCombination[],
  filters: SalesAttributionFilterState,
  changed: SalesAttributionUtmField
): SalesAttributionFilterState => {
  const next = { ...filters };
  UTM_FIELDS.filter(field => field !== changed).forEach(field => {
    const current = next[field];
    if (current && !getUtmOptions(combinations, field, next).some(option => option.value === current)) {
      next[field] = "";
    }
  });
  return next;
};
